import { FC, ReactNode, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/popover/index';
import { Tooltip } from '../../../components/tooltip/index';
import { QueryKey } from '../../../constants/index';
import {
  EventHooks,
  ScenarioHooks,
  SnapshotHooks,
  useFormat,
  useInvalidateQueries,
  useModalState,
} from '../../../hooks/index';
import {
  EventTypeBackend,
  EventUnion,
  SortByType,
  ValuationEvent,
} from '../../../types/events.types';
import { Scenario } from '../../../types/scenario-modeling.types';
import { Snapshot, SnapshotListItem } from '../../../types/snapshot.types';
import { useScenarioCapTableStore } from '../cap-table.store';

export type CapTableSelectProps = {
  companyId: string;
  scenario?: Scenario;
  selectedVariant: ReactNode | string;
  setSelected: (selectedVariant: ReactNode | string) => void;
  // Scenario creation only reads `valuation` figures, so the slim list row is enough here.
  setSnapshot: (snapshot: Snapshot | SnapshotListItem | undefined) => void;
};
type CapTableSelect = {
  date?: Date;
  id: string;
  name: ReactNode | string;
};
export const CapTableSelect: FC<CapTableSelectProps> = ({
  companyId,
  selectedVariant,
  setSelected,
  setSnapshot,
  scenario,
}) => {
  const { format } = useFormat();
  const { events } = EventHooks.useEvents({
    companyId,
    typesFilter: [SortByType.VALUATION],
  });
  const { isOpen, toggler } = useModalState();
  const valuationEvents = events.filter(
    (event: EventUnion) => event.type === EventTypeBackend.VALUATION,
  ) as ValuationEvent[];
  const { scheduleUpdate } = useScenarioCapTableStore();
  const selectOptions: CapTableSelect[] = useMemo(
    () => [
      {
        name: <AppFormattedMessage id="LATEST_CAP_TABLE" />,
        id: '',
      },
      ...valuationEvents,
    ],
    [valuationEvents],
  );

  const { snapshots } = SnapshotHooks.useSnapshots({
    companyId,
    capTableMode: true,
  });

  const { snapshot: currentSnapshot } = SnapshotHooks.useCurrent({
    companyId,
    capTableMode: true,
  });

  const { update } = ScenarioHooks.useUpdate();

  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_SCENARIO);

  const handleValuationSelect = ({ id, name }: CapTableSelect) => {
    setSelected(name);
    toggler(false);
    if (!scenario) return;
    if (!id) {
      setSnapshot(currentSnapshot);
      scheduleUpdate(scenario.id);
      update(
        {
          scenarioId: scenario.id,
          data: {
            currentCapTable: true,
            preMoneyValuation: 0,
            closingDate: null,
          },
        },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      );
    } else {
      const selectedSnapshot = snapshots.find(({ valuation }) => id === valuation?.id);
      setSnapshot(selectedSnapshot);
      const preMoneyVal =
        (selectedSnapshot?.valuation?.issuedSharesOnStart || 0) *
        (selectedSnapshot?.valuation?.sharePrice || 0);
      scheduleUpdate(scenario.id);

      update(
        {
          scenarioId: scenario.id,
          data: {
            valuationId: selectedSnapshot?.valuation?.id || '',
            preMoneyValuation: preMoneyVal,
            closingDate: null,
          },
        },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      );
    }
  };

  return (
    <>
      <Popover onOpenChange={toggler} open={isOpen}>
        <PopoverTrigger
          className={twMerge(
            'flex w-[162px] items-center justify-between gap-2 rounded border border-gray-200 px-3 py-2 text-sm font-[450] text-gray-700 transition-colors',
            isOpen ? 'bg-gray-50' : 'bg-gray-25',
          )}
        >
          <span className="truncate text-sm font-[450] text-gray-700">{selectedVariant}</span>
          <ChevronDownIcon
            className={twMerge(
              'h-4 w-4 text-gray-400',
              isOpen ? 'rotate-180' : '',
              'transition-transform',
            )}
          />
        </PopoverTrigger>
        <PopoverContent className="flex max-h-[400px] w-[162px] flex-col gap-2 overflow-y-auto text-nowrap rounded p-2 shadow-xl lg:max-w-[700px]">
          {selectOptions.map((data) => (
            <span
              className={twMerge(
                'flex w-full cursor-pointer flex-col rounded px-2 py-[6px] text-sm text-gray-700 hover:bg-brand-25',
              )}
              key={`${data.id}_${data.name}`}
              onClick={() => handleValuationSelect(data)}
            >
              <Tooltip content={data.name}>
                <p className="truncate">{data.name}</p>
              </Tooltip>

              {data.date && (
                <p className="text-label-sm font-medium text-gray-500">
                  {format(new Date(data.date), 'dd/MM/yyyy')}
                </p>
              )}
            </span>
          ))}
        </PopoverContent>
      </Popover>
    </>
  );
};
